// src/services/ReceiptScanService.js
/**
 * レシート画像を処理し、情報を抽出するサービス。
 * 実際のOCR処理はGemini APIを呼び出すことで行われます。
 */

/**
 * Base64エンコードされたレシート画像をOCRで処理し、取引データを抽出します。
 * @param {string} base64ImageData - Base64エンコードされたレシート画像データ (例: "data:image/png;base64,iVBORw0K...")
 * @returns {Promise<Object>} 抽出された取引データ (date, amount, merchant, category, descriptionなど)
 * @throws {Error} API呼び出しまたはデータ処理に失敗した場合
 */
export const processReceiptImage = async (base64ImageData) => {
    // base64ImageDataがnullまたは空の場合のチェック
    if (!base64ImageData) {
        throw new Error("処理する画像データが提供されていません。");
    }

    // "data:image/png;base64," のようなプレフィックスを削除
    const base64Content = base64ImageData.split(',')[1];
    if (!base64Content) {
        throw new Error("無効な画像データ形式です。");
    }

    try {
        // API呼び出しの準備
        // レシートから日付、合計金額、店舗名、推測される日本語のカテゴリと簡潔な内容を抽出するようプロンプトを定義
        const prompt = "Extract the total amount, date (format YYYY-MM-DD), and merchant name from this receipt image. Also, try to infer a Japanese category (e.g., '飲食費', '交通費', '消耗品費', '売上') and a brief description based on the items. Provide the output in JSON format, ensuring 'date', 'amount', 'merchant' are always present. Example: { 'date': 'YYYY-MM-DD', 'amount': 1234.56, 'merchant': 'Shop Name', 'category': 'Category Name', 'description': 'Item Description' }";
        
        const payload = {
            contents: [
                {
                    role: "user",
                    parts: [
                        { text: prompt },
                        {
                            inlineData: {
                                mimeType: "image/jpeg", // または "image/png" 画像のMIMEタイプに合わせて調整してください
                                data: base64Content
                            }
                        }
                    ]
                }
            ],
            generationConfig: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: "OBJECT",
                    properties: {
                        "date": { "type": "STRING" },
                        "amount": { "type": "NUMBER" },
                        "merchant": { "type": "STRING" },
                        "category": { "type": "STRING" },
                        "description": { "type": "STRING" }
                    },
                    required: ["date", "amount", "merchant"] // 必須フィールド
                }
            }
        };

        const apiKey = ""; // Canvas環境が自動的に提供するため、空文字列のままにしておきます
        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

        // 実際のAPI呼び出し
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorBody = await response.text();
            throw new Error(`APIリクエストが失敗しました: ${response.status} ${response.statusText} - ${errorBody}`);
        }

        const result = await response.json();

        if (result.candidates && result.candidates.length > 0 && result.candidates[0].content && result.candidates[0].content.parts && result.candidates[0].content.parts.length > 0) {
            const jsonString = result.candidates[0].content.parts[0].text;
            const parsedData = JSON.parse(jsonString);

            // 抽出データのバリデーション (必要に応じて追加)
            if (!parsedData.date || typeof parsedData.amount === 'undefined' || !parsedData.merchant) {
                throw new Error("APIレスポンスから必須情報 (日付、金額、店舗名) を抽出できませんでした。");
            }
            return parsedData;
        } else {
            throw new Error("APIからの応答が期待された形式ではありませんでした。");
        }

    } catch (err) {
        console.error('ReceiptScanServiceでのエラー:', err);
        // エラーを再スローして呼び出し元で処理させる
        throw new Error(`レシート処理中にエラーが発生しました: ${err.message}`);
    }
};
